import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ChevronLeft, ChevronRight, Download, Upload, Play, Plus, X } from 'lucide-react';
import { emptyJourney, createNewJourney } from '../data/journeyTemplate';
import { journeyToStoryData, journeyToTweeCode } from '../utils/journeyToStory';
import JourneyAIAssistant from './JourneyAIAssistant';
import JourneyTour from './JourneyTour';
import SEO from './SEO';
import PageHeader from './PageHeader';

const JourneyWizard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  
  // Initialize journey data from localStorage or use default
  const [journeyData, setJourneyData] = useState(() => {
    const saved = localStorage.getItem('journey_wizard_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.problems && parsed.metadata?.wizard_version === '2.0.0') {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to load saved state:', e);
      }
    }
    return emptyJourney;
  });
  
  // Initialize tour state from localStorage
  const [showTour, setShowTour] = useState(() => {
    const tourShown = localStorage.getItem('journey_wizard_tour_shown');
    const dontShow = localStorage.getItem('journey_wizard_dont_show_tour');
    if (!tourShown && !dontShow) {
      localStorage.setItem('journey_wizard_tour_shown', 'true');
      return true;
    }
    return false;
  });
  
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [dontShowTourAgain, setDontShowTourAgain] = useState(false);

  // Save to localStorage on change
  useEffect(() => {
    if (journeyData.metadata.project_name) {
      localStorage.setItem('journey_wizard_state', JSON.stringify(journeyData));
    }
  }, [journeyData]);

  const updateJourneyData = (path, value) => {
    setJourneyData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      newData.metadata.updated_at = new Date().toISOString();
      
      return newData;
    });
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(journeyData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${journeyData.metadata.project_name || 'journey'}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({ title: 'Exported!', description: 'Journey data downloaded as JSON.' });
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target?.result);
        if (imported.metadata && imported.metadata.wizard_version) {
          setJourneyData(imported);
          toast({ title: 'Imported!', description: 'Journey data loaded successfully.' });
        } else {
          toast({ title: 'Invalid file', description: 'This does not appear to be a valid journey export.', variant: 'destructive' });
        }
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to parse file.', variant: 'destructive' });
      }
    };
    reader.readAsText(file);
  };

  const handleExportTwee = () => {
    const tweeCode = journeyToTweeCode(journeyData);
    const blob = new Blob([tweeCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${journeyData.metadata.project_name || 'journey'}-story.twee`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast({ title: 'Twee Story Exported!', description: 'Import this into Visual Story Planner.' });
  };

  const steps = [
    { title: 'Start', component: Step0 },
    { title: 'Problems', component: Step1 },
    { title: 'Persona', component: StepPersona },
    { title: 'Features', component: Step2 },
    { title: 'Story Arc', component: Step3 },
    { title: 'Journey', component: Step4 },
    { title: 'Export', component: Step5 }
  ];

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* SEO Meta Tags */}
      <SEO 
        title="Customer Journey Wizard - Map Your Product Story | TweePorter"
        description="Create compelling customer journey maps with AI assistance. Define problems, personas, features, and generate interactive stories that demonstrate your product's value."
        canonicalUrl="/journey-wizard"
        keywords="customer journey map, product story, value proposition, customer persona, interactive business story"
      />
      
      <PageHeader />
      
      {showTour && (
        <JourneyTour 
          onClose={() => setShowTour(false)} 
          dontShowAgain={dontShowTourAgain}
          setDontShowAgain={setDontShowTourAgain}
        />
      )}
      
      <div className="max-w-4xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900">Customer Journey Wizard</h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Turn your product ideas into clear customer stories</p>
            </div>
            <Button variant="outline" onClick={() => setShowTour(true)} className="w-full sm:w-auto">
              Take Tour
            </Button>
          </div>
        </div>

        {/* Progress Stepper - Mobile: Simplified, Desktop: Full */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Mobile View: Current Step Only */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-600">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-xs font-semibold text-orange-600">{steps[currentStep].title}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Desktop View: Full Stepper */}
          <div className="hidden sm:flex items-center justify-between overflow-x-auto">
            {steps.map((step, idx) => (
              <div key={idx} className="flex items-center min-w-0">
                <div className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full border-2 flex-shrink-0 ${
                  idx === currentStep ? 'border-orange-500 bg-orange-500 text-white' :
                  idx < currentStep ? 'border-green-500 bg-green-500 text-white' :
                  'border-slate-300 text-slate-400'
                }`}>
                  <span className="text-xs md:text-sm">{idx + 1}</span>
                </div>
                <span className={`ml-1 md:ml-2 text-xs md:text-sm truncate ${idx === currentStep ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                  {step.title}
                </span>
                {idx < steps.length - 1 && (
                  <div className={`w-6 md:w-12 h-0.5 mx-1 md:mx-2 flex-shrink-0 ${idx < currentStep ? 'bg-green-500' : 'bg-slate-300'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* AI Assistant - Below Stepper */}
        <JourneyAIAssistant 
          activeFieldId={activeFieldId}
          currentStep={currentStep}
          journeyData={journeyData}
        />

        {/* Main Content */}
        <Card className="mb-24 sm:mb-28">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl">{steps[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <CurrentStepComponent 
              data={journeyData} 
              updateData={updateJourneyData}
              onExport={handleExport}
              onImport={handleImport}
              onExportTwee={handleExportTwee}
              setActiveFieldId={setActiveFieldId}
            />
          </CardContent>
        </Card>

        {/* Navigation - Larger Buttons with Clear Labels */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-300 p-3 sm:p-4 shadow-2xl z-30">
          <div className="max-w-4xl mx-auto flex justify-between items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="flex-1 max-w-[180px] h-14 sm:h-16 text-base sm:text-lg font-semibold border-2"
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Back
            </Button>
            
            <Button 
              onClick={handleExport} 
              variant="outline" 
              className="h-14 sm:h-16 px-4 sm:px-6 text-sm sm:text-base font-semibold border-2"
            >
              <Download className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">Save</span>
            </Button>

            <Button
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              disabled={currentStep === steps.length - 1}
              className="flex-1 max-w-[180px] h-14 sm:h-16 text-base sm:text-lg font-semibold bg-orange-500 hover:bg-orange-600"
            >
              Next
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 ml-2" />
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

// Step 0: Start / Load Project
const Step0 = ({ data, updateData, onImport }) => {
  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="project_name">Project Name</Label>
        <Input
          id="project_name"
          value={data.metadata.project_name}
          onChange={(e) => updateData('metadata.project_name', e.target.value)}
          placeholder="e.g., SaaS Product Journey"
          onClick={(e) => e.currentTarget.dataset.fieldName = 'Project Name'}
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="font-semibold mb-4">Or Resume Previous Journey</h3>
        <Label htmlFor="file-upload" className="cursor-pointer">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-orange-500 transition-colors">
            <Upload className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <p className="text-sm text-slate-600">Click to upload previous journey JSON</p>
          </div>
          <input
            id="file-upload"
            type="file"
            accept=".json"
            onChange={onImport}
            className="hidden"
          />
        </Label>
      </div>
    </div>
  );
};

// Step 1: Problem Statements (New First Step)
const Step1 = ({ data, updateData, setActiveFieldId }) => {
  // Ensure problems array exists
  if (!data.problems || !Array.isArray(data.problems)) {
    updateData('problems', [{
      statement: '',
      cost_amount: '',
      cost_justification: '',
      tam_amount: '',
      tam_justification: '',
      current_solutions_fail: '',
      market_opportunity: '',
      expected_moat: '',
      solutions: []
    }]);
    return <div>Loading...</div>;
  }

  const addProblem = () => {
    if (data.problems.length < 3) {
      const newProblems = [...data.problems, {
        statement: '',
        cost_amount: '',
        cost_justification: '',
        tam_amount: '',
        tam_justification: '',
        current_solutions_fail: '',
        market_opportunity: '',
        expected_moat: '',
        solutions: []
      }];
      updateData('problems', newProblems);
    }
  };

  const removeProblem = (index) => {
    if (data.problems.length > 1) {
      const newProblems = data.problems.filter((_, idx) => idx !== index);
      updateData('problems', newProblems);
    }
  };

  const updateProblem = (index, field, value) => {
    const newProblems = [...data.problems];
    newProblems[index][field] = value;
    updateData('problems', newProblems);
  };

  const handleFieldClick = (e, fieldName) => {
    setActiveFieldId(e.target.id);
    e.target.dataset.fieldName = fieldName;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Guidance */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
        <h3 className="font-semibold text-blue-900 mb-2">How to Frame Your Problems</h3>
        <p className="text-sm text-blue-800 mb-2">Use this format: <strong>&quot;This product has a problem of Y&quot;</strong></p>
        <p className="text-xs text-blue-700 italic">
          Example: &quot;Small businesses have a problem of manual data entry that wastes 10 hours per week and costs $50K annually&quot;
        </p>
      </div>

      {/* Problem Cards - Horizontal Scroll on Mobile */}
      <div className="space-y-4">
        {data.problems.map((problem, idx) => (
          <Card key={idx} className="border-2 border-orange-300 bg-orange-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-orange-900">
                  Problem {idx + 1} {idx > 0 && <span className="text-sm font-normal text-orange-600">(Optional)</span>}
                </CardTitle>
                {idx > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => removeProblem(idx)} className="text-red-600">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Problem Statement */}
              <div>
                <Label htmlFor={`problem_${idx}_statement`} className="text-sm font-semibold">Problem Statement *</Label>
                <Textarea
                  id={`problem_${idx}_statement`}
                  value={problem.statement}
                  onChange={(e) => updateProblem(idx, 'statement', e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Statement`)}
                  placeholder="This product has a problem of..."
                  rows={3}
                  className="text-sm"
                />
              </div>

              {/* Cost - Horizontal Scroll Container */}
              <div>
                <Label className="text-sm font-semibold">Cost of Problem *</Label>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  <div className="flex-shrink-0 w-40">
                    <Label htmlFor={`problem_${idx}_cost_amount`} className="text-xs">$ Amount</Label>
                    <Input
                      id={`problem_${idx}_cost_amount`}
                      value={problem.cost_amount}
                      onChange={(e) => updateProblem(idx, 'cost_amount', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Cost Amount`)}
                      placeholder="$500K/year"
                      className="text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    <Label htmlFor={`problem_${idx}_cost_justification`} className="text-xs">Justification</Label>
                    <Input
                      id={`problem_${idx}_cost_justification`}
                      value={problem.cost_justification}
                      onChange={(e) => updateProblem(idx, 'cost_justification', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Cost Justification`)}
                      placeholder="Lost productivity, manual labor costs..."
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* TAM - Horizontal Scroll Container */}
              <div>
                <Label className="text-sm font-semibold">Market Size (TAM) *</Label>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  <div className="flex-shrink-0 w-40">
                    <Label htmlFor={`problem_${idx}_tam_amount`} className="text-xs">$ Amount</Label>
                    <Input
                      id={`problem_${idx}_tam_amount`}
                      value={problem.tam_amount}
                      onChange={(e) => updateProblem(idx, 'tam_amount', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} TAM Amount`)}
                      placeholder="$5B"
                      className="text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    <Label htmlFor={`problem_${idx}_tam_justification`} className="text-xs">Justification</Label>
                    <Input
                      id={`problem_${idx}_tam_justification`}
                      value={problem.tam_justification}
                      onChange={(e) => updateProblem(idx, 'tam_justification', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} TAM Justification`)}
                      placeholder="50M businesses × $100/year..."
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Why Current Solutions Fail */}
              <div>
                <Label htmlFor={`problem_${idx}_fail`} className="text-sm font-semibold">Why Current Solutions Fail *</Label>
                <Textarea
                  id={`problem_${idx}_fail`}
                  value={problem.current_solutions_fail}
                  onChange={(e) => updateProblem(idx, 'current_solutions_fail', e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Current Solutions Fail`)}
                  placeholder="Existing tools are too complex, expensive, or don't integrate..."
                  rows={3}
                  className="text-sm"
                />
              </div>

              {/* Market Opportunity */}
              <div>
                <Label htmlFor={`problem_${idx}_opportunity`} className="text-sm font-semibold">Market Opportunity *</Label>
                <Textarea
                  id={`problem_${idx}_opportunity`}
                  value={problem.market_opportunity}
                  onChange={(e) => updateProblem(idx, 'market_opportunity', e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Market Opportunity`)}
                  placeholder="There is an opportunity because..."
                  rows={3}
                  className="text-sm"
                />
              </div>

              {/* Expected Moat */}
              <div>
                <Label htmlFor={`problem_${idx}_moat`} className="text-sm font-semibold">Expected Moat *</Label>
                <Textarea
                  id={`problem_${idx}_moat`}
                  value={problem.expected_moat}
                  onChange={(e) => updateProblem(idx, 'expected_moat', e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Problem ${idx + 1} Expected Moat`)}
                  placeholder="Our competitive advantage will be..."
                  rows={3}
                  className="text-sm"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Problem Button */}
      {data.problems.length < 3 && (
        <Button 
          onClick={addProblem} 
          variant="outline" 
          className="w-full border-2 border-dashed border-orange-400 text-orange-600 hover:bg-orange-50"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Another Problem (up to 3 total)
        </Button>
      )}
    </div>
  );
};

// Step 2: Persona (moved from old Step 1)
const StepPersona = ({ data, updateData, setActiveFieldId }) => {
  const handleFieldClick = (e, fieldName) => {
    setActiveFieldId(e.target.id);
    e.target.dataset.fieldName = fieldName;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <Label htmlFor="persona_name" className="text-sm sm:text-base">Persona Name</Label>
        <Input
          id="persona_name"
          value={data.persona.name}
          onChange={(e) => updateData('persona.name', e.target.value)}
          onClick={(e) => handleFieldClick(e, 'Persona Name')}
          placeholder="e.g., Sarah the Startup Founder"
          className="text-sm sm:text-base h-10 sm:h-auto"
        />
      </div>

      <div>
        <Label htmlFor="persona_role" className="text-sm sm:text-base">Role / Segment</Label>
        <Input
          id="persona_role"
          value={data.persona.role}
          onChange={(e) => updateData('persona.role', e.target.value)}
          onClick={(e) => handleFieldClick(e, 'Role / Segment')}
          placeholder="e.g., SaaS founder, 10-person team"
          className="text-sm sm:text-base h-10 sm:h-auto"
        />
      </div>
    </div>
  );
};

// Step 3: Features & Solutions (problem-centric)
const Step2 = ({ data, updateData, setActiveFieldId }) => {
  const handleFieldClick = (e, fieldName) => {
    setActiveFieldId(e.target.id);
    e.target.dataset.fieldName = fieldName;
  };

  // Ensure features array exists
  const features = data.features || [
    { name: '', value: '', evidence: '' },
    { name: '', value: '', evidence: '' },
    { name: '', value: '', evidence: '' }
  ];

  // Initialize features if missing
  if (!data.features) {
    updateData('features', features);
  }

  // Get problems for linking solutions
  const problems = data.problems || [];

  // Add a solution to a specific problem
  const addSolution = (problemIdx) => {
    const newProblems = [...problems];
    if (!newProblems[problemIdx].solutions) {
      newProblems[problemIdx].solutions = [];
    }
    newProblems[problemIdx].solutions.push({ name: '', description: '' });
    updateData('problems', newProblems);
  };

  // Update a solution for a specific problem
  const updateSolution = (problemIdx, solutionIdx, field, value) => {
    const newProblems = [...problems];
    newProblems[problemIdx].solutions[solutionIdx][field] = value;
    updateData('problems', newProblems);
  };

  // Remove a solution from a specific problem
  const removeSolution = (problemIdx, solutionIdx) => {
    const newProblems = [...problems];
    newProblems[problemIdx].solutions = newProblems[problemIdx].solutions.filter((_, idx) => idx !== solutionIdx);
    updateData('problems', newProblems);
  };

  return (
    <div className="space-y-8">
      {/* Product Statement */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <Label htmlFor="product_statement" className="text-base font-semibold text-blue-900">
          General Product Statement
        </Label>
        <p className="text-sm text-blue-700 mb-3">What does your product do in one sentence?</p>
        <Textarea
          id="product_statement"
          value={data.product_statement || ''}
          onChange={(e) => updateData('product_statement', e.target.value)}
          onClick={(e) => handleFieldClick(e, 'Product Statement')}
          placeholder="e.g., We help small businesses automate their invoicing workflow..."
          rows={2}
        />
      </div>

      {/* Problem-Specific Solutions */}
      <div>
        <h3 className="font-semibold text-lg mb-2">Solutions for Each Problem</h3>
        <p className="text-sm text-slate-600 mb-4">
          Add features/solutions that address each problem you defined in Step 1
        </p>

        {problems.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
            No problems defined yet. Go back to the Problems step to add some.
          </div>
        ) : (
          problems.map((problem, problemIdx) => (
            <Card key={problemIdx} className="mb-6 border-2 border-teal-200">
              <CardHeader className="pb-2 bg-teal-50">
                <CardTitle className="text-base text-teal-900">
                  Problem {problemIdx + 1}: {problem.statement ? problem.statement.substring(0, 80) + (problem.statement.length > 80 ? '...' : '') : 'Not defined'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {/* Solutions for this problem */}
                {(problem.solutions || []).map((solution, solutionIdx) => (
                  <div key={solutionIdx} className="bg-white border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Solution {solutionIdx + 1}</Label>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeSolution(problemIdx, solutionIdx)}
                        className="text-red-500 hover:text-red-700 h-6 px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input
                      id={`solution_${problemIdx}_${solutionIdx}_name`}
                      value={solution.name}
                      onChange={(e) => updateSolution(problemIdx, solutionIdx, 'name', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${problemIdx + 1} Solution ${solutionIdx + 1} Name`)}
                      placeholder="Feature/Solution name"
                      className="text-sm"
                    />
                    <Textarea
                      id={`solution_${problemIdx}_${solutionIdx}_desc`}
                      value={solution.description}
                      onChange={(e) => updateSolution(problemIdx, solutionIdx, 'description', e.target.value)}
                      onClick={(e) => handleFieldClick(e, `Problem ${problemIdx + 1} Solution ${solutionIdx + 1} Description`)}
                      placeholder="How does this solve the problem?"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                ))}

                {/* Add Solution Button */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => addSolution(problemIdx)}
                  className="w-full border-dashed border-teal-400 text-teal-600 hover:bg-teal-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Solution for This Problem
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* General Features Section */}
      <div className="border-t pt-6">
        <h3 className="font-semibold text-lg mb-2">General Product Features</h3>
        <p className="text-sm text-slate-600 mb-4">
          Additional key features (not specific to a single problem)
        </p>
        
        {features.map((feature, idx) => (
          <Card key={idx} className="mb-4">
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor={`feature_name_${idx}`}>Feature {idx + 1} Name</Label>
                <Input
                  id={`feature_name_${idx}`}
                  value={feature.name}
                  onChange={(e) => updateData(`features.${idx}.name`, e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Feature ${idx + 1} Name`)}
                  placeholder="e.g., Real-time Collaboration"
                />
              </div>

              <div>
                <Label htmlFor={`feature_value_${idx}`}>What value does this create?</Label>
                <Textarea
                  id={`feature_value_${idx}`}
                  value={feature.value}
                  onChange={(e) => updateData(`features.${idx}.value`, e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Feature ${idx + 1} Value`)}
                  placeholder="How does this benefit the customer?"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor={`feature_evidence_${idx}`}>Evidence / Proof</Label>
                <Textarea
                  id={`feature_evidence_${idx}`}
                  value={feature.evidence}
                  onChange={(e) => updateData(`features.${idx}.evidence`, e.target.value)}
                  onClick={(e) => handleFieldClick(e, `Feature ${idx + 1} Evidence`)}
                  placeholder="Metrics, testimonials, case studies..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Step 3: Story Arc
const Step3 = ({ data, updateData, setActiveFieldId }) => {
  const handleFieldClick = (e, fieldName) => {
    setActiveFieldId(e.target.id);
    e.target.dataset.fieldName = fieldName;
  };

  // Ensure story_arc exists
  const story_arc = data.story_arc || {
    hero: '', quest: '', villains: '', guide: '', moment_of_truth: '', success: ''
  };

  const questions = [
    { key: 'hero', label: 'Who is the hero?', placeholder: 'Usually your customer persona' },
    { key: 'quest', label: 'What is their quest?', placeholder: 'What outcome do they want?' },
    { key: 'villains', label: 'What villains block them?', placeholder: 'Pains, friction, obstacles' },
    { key: 'guide', label: 'What guides them?', placeholder: 'Your product and key features' },
    { key: 'moment_of_truth', label: 'What is the moment of truth?', placeholder: 'When they realize your solution is best' },
    { key: 'success', label: 'What does success look like?', placeholder: 'Tangible outcome after your solution' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">Dan Roam Story Arc</h3>
        <p className="text-sm text-blue-700">Answer these questions to create a compelling narrative</p>
      </div>

      {questions.map((q, idx) => (
        <div key={q.key}>
          <Label htmlFor={`story_${q.key}`}>{q.label}</Label>
          <Textarea
            id={`story_${q.key}`}
            value={story_arc[q.key] || ''}
            onChange={(e) => updateData(`story_arc.${q.key}`, e.target.value)}
            onClick={(e) => handleFieldClick(e, q.label)}
            placeholder={q.placeholder}
            rows={2}
          />
        </div>
      ))}
    </div>
  );
};

// Step 4: Journey Stages
const Step4 = ({ data, updateData, setActiveFieldId }) => {
  const handleFieldClick = (e, fieldName) => {
    setActiveFieldId(e.target.id);
    e.target.dataset.fieldName = fieldName;
  };

  // Ensure stages array exists
  const stages = data.stages || [
    { name: 'Awareness', action: '', thoughts: '', feeling: '' },
    { name: 'Consideration', action: '', thoughts: '', feeling: '' },
    { name: 'Trial', action: '', thoughts: '', feeling: '' },
    { name: 'Purchase', action: '', thoughts: '', feeling: '' },
    { name: 'Onboarding', action: '', thoughts: '', feeling: '' },
    { name: 'Adoption', action: '', thoughts: '', feeling: '' },
    { name: 'Advocacy', action: '', thoughts: '', feeling: '' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-purple-900 mb-2">Customer Journey Stages</h3>
        <p className="text-sm text-purple-700">Describe what happens at each stage of the journey</p>
      </div>

      {stages.map((stage, idx) => (
        <Card key={idx}>
          <CardHeader>
            <CardTitle className="text-lg">{stage.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor={`stage_action_${idx}`}>What is the customer trying to do?</Label>
              <Textarea
                id={`stage_action_${idx}`}
                value={stage.action || ''}
                onChange={(e) => updateData(`stages.${idx}.action`, e.target.value)}
                onClick={(e) => handleFieldClick(e, `${stage.name} - Action`)}
                placeholder="Customer actions at this stage..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor={`stage_thoughts_${idx}`}>What are they thinking or asking?</Label>
              <Textarea
                id={`stage_thoughts_${idx}`}
                value={stage.thoughts || ''}
                onChange={(e) => updateData(`stages.${idx}.thoughts`, e.target.value)}
                onClick={(e) => handleFieldClick(e, `${stage.name} - Thoughts`)}
                placeholder="Questions, doubts, considerations..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor={`stage_feeling_${idx}`}>How do they feel?</Label>
              <Input
                id={`stage_feeling_${idx}`}
                value={stage.feeling || ''}
                onChange={(e) => updateData(`stages.${idx}.feeling`, e.target.value)}
                onClick={(e) => handleFieldClick(e, `${stage.name} - Feeling`)}
                placeholder="e.g., Curious, Anxious, Excited..."
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Step 5: Export & Summary
const Step5 = ({ data, onExportTwee }) => {
  const { toast } = useToast();

  // Get problems safely
  const problems = data.problems || [];
  const features = data.features || [];

  // Get the first problem statement as the main problem (for backward compat)
  const mainProblem = problems.length > 0 ? problems[0].statement : 'Not specified';

  const handleCopySummary = () => {
    // Build problems summary
    const problemsSummary = problems
      .filter(p => p.statement)
      .map((p, i) => `  ${i + 1}. ${p.statement}`)
      .join('\n') || '  No problems defined';

    // Build solutions summary
    const solutionsSummary = problems
      .filter(p => p.solutions && p.solutions.length > 0)
      .map((p, i) => {
        const solutions = p.solutions.map(s => `    - ${s.name}: ${s.description}`).join('\n');
        return `  Problem ${i + 1} Solutions:\n${solutions}`;
      })
      .join('\n') || '  No solutions defined';

    const summary = `
Customer Journey Summary
========================

Persona: ${data.persona?.name || 'Not specified'} - ${data.persona?.role || 'Not specified'}

Problems:
${problemsSummary}

Product Statement: ${data.product_statement || 'Not specified'}

Solutions:
${solutionsSummary}

Story Arc:
- Hero: ${data.story_arc?.hero || 'Not specified'}
- Quest: ${data.story_arc?.quest || 'Not specified'}
- Success: ${data.story_arc?.success || 'Not specified'}

Key Features:
${features.filter(f => f.name).map((f, i) => `${i + 1}. ${f.name}: ${f.value}`).join('\n') || 'No features defined'}

Journey Stages: ${(data.stages || []).filter(s => s.action).length} stages defined
    `.trim();

    navigator.clipboard.writeText(summary);
    toast({ title: 'Copied!', description: 'Journey summary copied to clipboard' });
  };

  return (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="font-semibold text-green-900 text-lg mb-4">Journey Map Complete!</h3>
        <p className="text-green-700 mb-4">Your customer journey has been mapped. Here&apos;s what you can do next:</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={onExportTwee}>
          <CardContent className="pt-6">
            <Play className="h-8 w-8 text-orange-500 mb-3" />
            <h4 className="font-semibold mb-2">Export Twee Story</h4>
            <p className="text-sm text-slate-600">Download a .twee file ready for Visual Story Planner</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleCopySummary}>
          <CardContent className="pt-6">
            <Download className="h-8 w-8 text-blue-500 mb-3" />
            <h4 className="font-semibold mb-2">Copy Summary</h4>
            <p className="text-sm text-slate-600">Get a text summary for docs and presentations</p>
          </CardContent>
        </Card>
      </div>

      <div className="border-t pt-6">
        <h4 className="font-semibold mb-4">Journey Preview</h4>
        <div className="bg-slate-50 rounded-lg p-4 space-y-4">
          <div>
            <span className="font-semibold text-slate-700">Persona:</span>
            <p className="text-slate-600">{data.persona?.name || 'Not specified'} - {data.persona?.role || 'Not specified'}</p>
          </div>
          <div>
            <span className="font-semibold text-slate-700">Problems:</span>
            {problems.length > 0 ? (
              <ul className="list-disc list-inside text-slate-600">
                {problems.filter(p => p.statement).map((p, i) => (
                  <li key={i}>{p.statement.substring(0, 100)}{p.statement.length > 100 ? '...' : ''}</li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600">No problems defined</p>
            )}
          </div>
          <div>
            <span className="font-semibold text-slate-700">Product Statement:</span>
            <p className="text-slate-600">{data.product_statement || 'Not specified'}</p>
          </div>
          <div>
            <span className="font-semibold text-slate-700">Features:</span>
            <ul className="list-disc list-inside text-slate-600">
              {features.filter(f => f.name).map((f, i) => (
                <li key={i}>{f.name}</li>
              ))}
              {features.filter(f => f.name).length === 0 && <li>No features defined</li>}
            </ul>
          </div>
          <div>
            <span className="font-semibold text-slate-700">Journey Stages:</span>
            <p className="text-slate-600">{(data.stages || []).filter(s => s.action).length} stages completed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyWizard;
